import { useState } from 'react';

import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

import {
    IImageBaseViewModel,
    IProductItemViewModel,
    IProductPageViewModel,
    IWishListTranslations,
} from 'Types/Litium';
import { ISrcSetSize } from 'Types/shared';
import ProductCardImage from 'Components/ProductCard/ProductCardImage';
import ProductCardInfo from 'Components/ProductCard/ProductCardInfo';
import ProductCardInfoQuickBuy from 'Components/ProductCard/ProductCardInfoQuickBuy';
import VideoPlayer from 'Components/VideoPlayer';

export interface ProductCardProps {
    images: IProductItemViewModel['images'];
    colors: IProductItemViewModel['productFilters']['colors'];
    title?: string;
    sizes: IProductItemViewModel['productFilters']['sizes'];
    url: IProductItemViewModel['url'];
    videoUrl?: IProductItemViewModel['videoUrl'];
    translations: IProductPageViewModel['translations'];
    srcSetSizes?: ISrcSetSize;
    productTag: IProductItemViewModel['productTag'];
    priceVM?: IProductItemViewModel['priceVM'];
    trackingData: IProductItemViewModel['trackingData'];
}

interface IProductItem
    extends Omit<
        IProductItemViewModel,
        | 'brand'
        | 'color'
        | 'colorHex'
        | 'currency'
        | 'description'
        | 'variantThumbnails'
        | 'isInStock'
        | 'product'
        | 'quantityFieldId'
        | 'showBuyButton'
        | 'size'
        | 'stockStatusDescription'
        | 'stockStateText'
        | 'stockStatusHex'
        | 'useVariantUrl'
        | 'price'
    > {
    srcSetSizes?: ISrcSetSize;
    quickBuyProduct?: boolean;
    colors?: IProductItemViewModel['productFilters']['colors'];
    values?: IProductItemViewModel['productFilters']['values'];
    sizes?: IProductItemViewModel['productFilters']['sizes'];
    additionalTranslations?: IWishListTranslations;
}

interface ProductItem
    extends Omit<IProductItem, 'variantSystemId' | 'feedImage'> {
    lazy?: boolean;
    spanImage?: boolean;
    subgrid?: boolean;
    showSizes?: boolean;
    quickBuyProduct?: boolean;
    colors?: IProductItemViewModel['productFilters']['colors'];
    values?: IProductItemViewModel['productFilters']['values'];
    sizes?: IProductItemViewModel['productFilters']['sizes'];
    additionalTranslations?: IWishListTranslations;
    evenAspectRatio?: boolean;
}

const ProductCard = ({
    subgrid,
    spanImage,
    images,
    name,
    url,
    lazy,
    videoUrl,
    productTag,
    showSizes = true,
    productFilters,
    srcSetSizes = {
        default: { height: 228, width: 160 },
        md: { height: 509, width: 356 },
        lg: { height: 305, width: 212 },
        xl: {
            height: 664,
            width: 465,
        },
        xxl: {
            height: 664,
            width: 465,
        },
    },
    priceVM,
    hasAnyVariantEnabled,
    translations,
    additionalTranslations,
    descriptiveVariantName,
    trackingData,
    isOnWishList,
    enabledWishlist,
    id,
    isPurchasable,
    showQuantityField,
    quickBuyProduct,
    isGiftCard,
    wishListIconText,
    evenAspectRatio,
}: ProductItem) => {
    const [loadSecondaryImage, setLoadSecondaryImage] = useState(false);

    const handleProductCardClick = () => {
        window.dataLayer.push({
            event: 'ee_product_click',
            ecommerce: {
                click: {
                    actionField: {
                        list: 'Productcard',
                    },
                    products: [
                        {
                            name: trackingData.name,
                            id: trackingData.variantId,
                            price: trackingData.basePrice,
                            variant: trackingData.variant,
                        },
                    ],
                },
            },
        });
    };

    const videoPlaceholderSizes = [
        {
            breakpoint: tailwindBreakpoints.sm,
            width: srcSetSizes.default.width,
            height: srcSetSizes.default.height,
            src: images?.[0]?.src,
            altText: images?.[0]?.altText,
        },
        {
            breakpoint: tailwindBreakpoints.md,
            width: srcSetSizes.md.width,
            height: srcSetSizes.md.height,
            src: images?.[0]?.src,
            altText: images?.[0]?.altText,
        },
        {
            breakpoint: tailwindBreakpoints.lg,
            width: srcSetSizes.lg.width,
            height: srcSetSizes.lg.height,
            src: images?.[0]?.src,
            altText: images?.[0]?.altText,
        },
        {
            breakpoint: tailwindBreakpoints.xl,
            width: srcSetSizes.xl.width,
            height: srcSetSizes.xxl.height,
            src: images?.[0]?.src,
            altText: images?.[0]?.altText,
        },
        {
            breakpoint: tailwindBreakpoints.xxl,
            width: srcSetSizes.xxl.width,
            height: srcSetSizes.xxl.height,
            src: images?.[0]?.src,
            altText: images?.[0]?.altText,
        },
    ];

    return (
        <div
            className={`mb-2 flex w-full flex-col gap-3 ${
                subgrid ? 'supports-[grid-template-rows:_subgrid]:contents' : ''
            }`}
        >
            {videoUrl && (hasAnyVariantEnabled || isPurchasable) && videoUrl ? (
                <a
                    href={url}
                    aria-label={descriptiveVariantName}
                    className={`relative h-full w-full basis-full @container ${
                        spanImage ? 'lg:row-[span_3]' : ''
                    } ${evenAspectRatio ? 'aspect-square' : ''}`}
                >
                    <VideoPlayer
                        altDescription={
                            images?.[0]?.altText ?? descriptiveVariantName
                        }
                        autoPlay
                        showController={false}
                        placeholderSmall={images?.[0]}
                        placeholderLarge={images?.[0]}
                        videoPlaceholderSizes={videoPlaceholderSizes}
                        url={videoUrl}
                        placeholderAlt={
                            images?.[0]?.altText ?? descriptiveVariantName
                        }
                    />
                    {productTag?.src && (
                        <img
                            className=" absolute left-[12px] top-[12px] z-[1] w-8 supports-[width:15cqw]:w-[clamp(32px,_15cqw,_15cqw)] lg:w-14 lg:supports-[width:15cqw]:w-[clamp(32px,_15cqw,_15cqw)]"
                            src={productTag.src}
                            alt={productTag.altText}
                            width={productTag.width}
                            height={productTag.height}
                        />
                    )}
                </a>
            ) : (
                <a
                    href={url}
                    aria-label={descriptiveVariantName}
                    onClick={() => handleProductCardClick()}
                    onMouseEnter={() => setLoadSecondaryImage(true)}
                    className={`product-card @container ${
                        images.length > 1 ? 'group' : ''
                    } relative h-full w-full basis-full ${
                        spanImage
                            ? 'supports-[grid-template-rows:_subgrid]:lg:row-[span_3]'
                            : ''
                    } ${
                        hasAnyVariantEnabled || isPurchasable
                            ? ''
                            : 'before:absolute before:h-full before:w-full before:bg-white/50 before:backdrop-blur-[4px]'
                    }${evenAspectRatio ? 'aspect-square' : ''}`}
                >
                    {images[0] && (
                        <ProductCardImage
                            className="h-full w-full"
                            image={images[0]}
                            srcSetSizes={srcSetSizes}
                            lazy={lazy}
                            isSoldOut={!hasAnyVariantEnabled}
                            evenAspectRatio
                        />
                    )}

                    {loadSecondaryImage &&
                        hasAnyVariantEnabled &&
                        images.length > 1 && (
                            <ProductCardImage
                                className="invisible absolute left-0 top-0 h-full w-full opacity-0 transition-[opacity,_visibility] duration-layout group-hover:visible group-hover:opacity-100"
                                image={images[1]}
                                srcSetSizes={srcSetSizes}
                                isSoldOut={!hasAnyVariantEnabled}
                                evenAspectRatio
                            />
                        )}
                    {!hasAnyVariantEnabled && (
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -rotate-[10deg] text-center text-lg supports-[font-size:1cqw]:text-[clamp(1rem,_7.47cqw,18rem)]">
                            {translations.outOfStock}
                        </span>
                    )}
                    {productTag?.src && (
                        <img
                            className="absolute left-[12px] top-[12px] w-8 supports-[width:15cqw]:w-[clamp(32px,_15cqw,_15cqw)] lg:w-14 lg:supports-[width:15cqw]:w-[clamp(32px,_15cqw,_15cqw)]"
                            src={productTag.src}
                            alt={productTag.altText}
                            width={productTag.width}
                            height={productTag.height}
                        />
                    )}
                </a>
            )}
            {!quickBuyProduct ? (
                <div className="px-1.5 pb-2 lg:px-3">
                    <ProductCardInfo
                        showSizes={showSizes}
                        sizes={productFilters.sizes}
                        colors={productFilters.colors}
                        name={name}
                        priceVM={priceVM}
                        url={url}
                        isOnWishList={isOnWishList}
                        enabledWishlist={enabledWishlist}
                        id={id}
                        isGiftCard={isGiftCard}
                        wishListIconText={wishListIconText}
                    />
                </div>
            ) : (
                <ProductCardInfoQuickBuy
                    enabledWishlist={enabledWishlist}
                    additionalTranslations={additionalTranslations}
                    values={productFilters.values}
                    colors={productFilters.colors}
                    sizes={productFilters.sizes}
                    name={name}
                    priceVM={priceVM}
                    isOnWishList={isOnWishList}
                    showQuantityField={showQuantityField}
                    wishListIconText={wishListIconText}
                />
            )}
        </div>
    );
};

export default ProductCard;
